import React, { useEffect } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";

/**
 * Enterprise-styled confirmation dialog used by every mutation in the
 * Security Administration console. Supports a tone-aware CTA so
 * destructive actions render red and reversible ones render neutral.
 */
export default function ConfirmDialog({
    open, title = "Please confirm", message, tone = "neutral",
    confirmLabel = "Confirm", cancelLabel = "Cancel",
    busy = false, onCancel, onConfirm, testid = "admin-sec-confirm",
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape" && !busy) onCancel?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onCancel, busy]);

    if (!open) return null;
    const ctaCls =
        tone === "danger"
            ? "bg-[#DA3535] hover:bg-[#B91C1C] text-white"
            : "bg-[#131B2F] hover:bg-[#0B1220] text-white";

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid={testid}
        >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[#131B2F] font-semibold text-sm">
                        <AlertTriangle size={16} className="text-[#B45309]" /> {title}
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                        aria-label="Close"
                    >
                        <X size={14} />
                    </button>
                </div>
                <div className="px-6 py-5 text-sm text-[#4B5565] leading-relaxed">
                    {message}
                </div>
                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                        data-testid={`${testid}-cancel`}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={busy}
                        className={`h-10 px-4 rounded-md text-sm font-medium disabled:opacity-70 inline-flex items-center gap-2 ${ctaCls}`}
                        data-testid={`${testid}-ok`}
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
